/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import booleanIntersects from '@turf/boolean-intersects';
import * as turf from '@turf/turf';
import memoize from "memoizee";
import { v4 as uuid } from "uuid";
import { parse } from "wkt";

import { ILatLon } from "../../../Models/ILatLon";
import { IPointBase } from '../../../Models/IPoint';
import { badPolygon, goodPolygon } from "../Constants/StyleConstants";
import { PolygonType } from "../Models/Maps/Enums/PolygonType";
import { ICircle } from "../Models/Maps/ICircle";
import { ICustomPolygon } from "../Models/Maps/ICustomPolygon";
import { IPolygon } from "../Models/Maps/IPolygon";
import { IPolygonColors } from "../Models/Maps/IPolygonColors";
import { IRectangle } from "../Models/Maps/IRectangle";
import booleanWithin from '@turf/boolean-within';

export const MOBILYTICS_MAX_DEFAULT_CIRCLE_RADIUS = 200_000;
export const MOBILYTICS_MAX_DEFAULT_RECTANGLE_LAT_LON_DIFF = 3;

export const SQ_METERS_PER_SQ_MILE = 2_589_975.2356;
export const MOBILYTICS_MAX_POLYGON_AREA_SQ_MILES = 3;
export const MOBILYTICS_MAX_POLYGON_AREA_SQ_METERS = MOBILYTICS_MAX_POLYGON_AREA_SQ_MILES * SQ_METERS_PER_SQ_MILE;

export const getPolygonType = (polygon: IPolygon): PolygonType => {

    const tryCustom = polygon as ICustomPolygon;
    if (tryCustom?.points) {
        return PolygonType.Custom;
    }

    const tryCircle = polygon as ICircle;
    if (tryCircle?.center) {
        return PolygonType.Circle;
    }

    const tryRectangle = polygon as IRectangle;
    if (tryRectangle?.northeast) {
        return PolygonType.Rectangle;
    }

    return PolygonType.Unknown;
};

export const convertToChildType = <T extends IPolygon>(polygon: IPolygon): T => {
    return polygon as T;
};

export const googleLatLngsAreEqual = (c1: google.maps.LatLng, c2: google.maps.LatLng): boolean => {
    return c1 && c2 && c1?.lat() === c2?.lat() && c1?.lng() === c2?.lng();
};

export const circlesAreEqual = (c1: ICircle, c2: ICircle): boolean => {
    return (
        c1
		&& c2
		&& c1?.radiusMeters === c2?.radiusMeters
		&& googleLatLngsAreEqual(c1?.center, c2?.center)
    );
};

export const rectanglesAreEqual = (r1: IRectangle, r2: IRectangle): boolean => {
    return (
        r1
		&& r2
		&& googleLatLngsAreEqual(r1?.northeast, r2?.northeast)
		&& googleLatLngsAreEqual(r1?.southwest, r2?.southwest)
    );
};

export const customPolygonsAreEqual = (p1: ICustomPolygon, p2: ICustomPolygon): boolean => {
    if (!p1 || !p2 || p1?.points?.length !== p1?.points?.length) {
        return false;
    }

    for (let i = 0; i < p1.points.length; i++) {
        if (!googleLatLngsAreEqual(p1.points[i], p2.points[i])) {
            return false;
        }
    }

    return true;
};

export const polygonsAreEqual = (type: PolygonType, p1: IPolygon, p2: IPolygon): boolean => {
    switch (type) {
        case PolygonType.Rectangle: {
            const r1 = p1 as IRectangle;
            const r2 = p2 as IRectangle;
            return rectanglesAreEqual(r1, r2);
        }
        case PolygonType.Circle: {
            const c1 = p1 as ICircle;
            const c2 = p2 as ICircle;
            return circlesAreEqual(c1, c2);
        }
        case PolygonType.Custom: {
            const poly1 = p1 as ICustomPolygon;
            const poly2 = p2 as ICustomPolygon;
            return customPolygonsAreEqual(poly1, poly2);
        }
        default:
            return false;
    }
};

export const getDefaultCircleRadiusByZoom = (zoom: number): number => {
    //BJ - This formula pretty closely matches what I manually determined to be reasonable sizes at each zoom level
    return Math.min(5 * Math.pow(2, 22 - (zoom + 6)), MOBILYTICS_MAX_DEFAULT_CIRCLE_RADIUS);
};

export const getDefaultRectangleLatLonDiffByZoom = (zoom: number): number => {
    //BJ - This formula pretty closely matches what I manually determined to be reasonable sizes at each zoom level
    return Math.min(3.05176E-05 * Math.pow(2, 22 - (zoom + 6)), MOBILYTICS_MAX_DEFAULT_RECTANGLE_LAT_LON_DIFF);
};

export const defaultPolygon = (
    type: PolygonType,
    mapCenter: google.maps.LatLng,
    zoom: number
): ICircle | IRectangle | ICustomPolygon => {
    const guid = uuid();

    const polygonBase: IPolygon = {
        polygonType: type,
        id: guid,
        name: guid,
        borderColor: "black",
        borderSize: 2,
        isSelected: true,
    };
    const latLonDiff: number = getDefaultRectangleLatLonDiffByZoom(zoom);

    switch (type) {
        case PolygonType.Rectangle: {
            const rectangle: IRectangle = {
                ...polygonBase,
                northeast: new google.maps.LatLng(mapCenter.lat() - latLonDiff, mapCenter.lng() + latLonDiff),
                southwest: new google.maps.LatLng(mapCenter.lat() + latLonDiff, mapCenter.lng() - latLonDiff),
            };
            return rectangle;
        }
        case PolygonType.Circle: {
            const circle: ICircle = {
                ...polygonBase,
                center: new google.maps.LatLng(mapCenter.lat(), mapCenter.lng()),
                radiusMeters: getDefaultCircleRadiusByZoom(zoom),
            };
            return circle;
        }
        case PolygonType.Custom: {
            const points = [
                new google.maps.LatLng(mapCenter.lat() + latLonDiff, mapCenter.lng()),
                new google.maps.LatLng(mapCenter.lat() - latLonDiff, mapCenter.lng() - latLonDiff),
                new google.maps.LatLng(mapCenter.lat() - latLonDiff, mapCenter.lng() + latLonDiff),
                new google.maps.LatLng(mapCenter.lat() + latLonDiff, mapCenter.lng()),
            ];
            const custom: ICustomPolygon = {
                ...polygonBase,
                points,
            };
            return custom;
        }
    }
};

export const polygonToPoints = (polygon: IPolygon):ILatLon[] => {
    switch (getPolygonType(polygon)) {
        case PolygonType.Circle: {
            const circle = polygon as ICircle;
            return circleToPoints(circle);
        }
        case PolygonType.Rectangle: {
            const rectangle = polygon as IRectangle;
            return rectangleToPoints(rectangle);
        }
        case PolygonType.Custom: {
            const poly = polygon as ICustomPolygon;
            return customPolygonToPoints(poly);
        }
        default: {
            console.error("Invalid polygon type");
            return [];
        }
    }
};

export const customPolygonToPoints = (polygon: ICustomPolygon): ILatLon[] => {
    if (polygon?.points) {
        const points = polygon.points.map((point: google.maps.LatLng) => {
            const ll: ILatLon = {
                latitude: point.lat(),
                longitude: point.lng()
            };

            return ll;
        });

        return closePointArrayPolygon(points);
    }

    return [];
};

export const rectangleToPoints = (rectangle: IRectangle): ILatLon[] => {
    if (rectangle?.northeast && rectangle?.southwest) {

        const ne: ILatLon = {
            latitude: rectangle.northeast.lat(),
            longitude: rectangle.northeast.lng()
        };
        const sw: ILatLon = {
            latitude: rectangle.southwest.lat(),
            longitude: rectangle.southwest.lng()
        };
        const se: ILatLon = {
            latitude: sw.latitude,
            longitude: ne.longitude
        };
        const nw: ILatLon = {
            latitude: ne.latitude,
            longitude: sw.longitude
        };

        return [
            ne,
            se,
            sw,
            nw,
            ne
        ];
    }

    return [];
};

export const circleToPoints = (circle: ICircle): ILatLon[] => {
    if (circle?.center && circle?.radiusMeters > 0) {
        const center = [circle.center.lng(), circle.center.lat()];
        const centerPoint = turf.point(center);
        const radiusKM = circle.radiusMeters / 1000;
        const steps = 32;
        const polygon = turf.circle(centerPoint, radiusKM, steps, "kilometers");

        return polygon.geometry.coordinates[0].map((coordinate) => {
            const point: ILatLon = {
                latitude: coordinate[1],
                longitude: coordinate[0],
            };

            return point;
        });
    }

    return [];
};

export const pointsToPolygon = (points: ILatLon[]) => {
    const turfCoords: number[][] = points.map((point) => {
        return [point.longitude, point.latitude];
    });

    const poly = turf.polygon([turfCoords]);
    return poly;
};

export const areaFromPoints = (points: ILatLon[]): number => {
    if (points?.length < 4) {
        return 0;
    }

    const poly = pointsToPolygon(points);
    return turf.area(poly);
};

export const polygonTooBig = (polygon: IPolygon): boolean => {
    return polygonArea(polygon) > MOBILYTICS_MAX_POLYGON_AREA_SQ_METERS;
};

export const polygonArea = (polygon: IPolygon): number => {
    let points: ILatLon[];

    switch (polygon.polygonType) {
        case PolygonType.Rectangle: {
            const rectangle = convertToChildType<IRectangle>(polygon);
            points = rectangleToPoints(rectangle);
            break;
        }
        case PolygonType.Circle: {
            const circle = convertToChildType<ICircle>(polygon);
            points = circleToPoints(circle);
            break;
        }
        case PolygonType.Custom: {
            const poly = convertToChildType<ICustomPolygon>(polygon);
            points = customPolygonToPoints(poly);
            break;
        }
        default: {
            points = [];
        }
    }

    return areaFromPoints(points);
};

export const getPolygonColors = (polygon: IPolygon): IPolygonColors => {
    return polygonTooBig(polygon) || polygonSelfIntersects(polygon)
        ? { ...badPolygon }
        : { ...goodPolygon };
};

export const closePointArrayPolygon = (points: ILatLon[]): ILatLon[] => {

    const pointsCopy: ILatLon[] = [...points];
    const lastIndex: number = pointsCopy.length - 1;

    if (pointsCopy[0].latitude !== pointsCopy[lastIndex].latitude
        || pointsCopy[0].longitude !== pointsCopy[lastIndex].longitude) {
        const firstPointCopy = {...pointsCopy[0]};
        pointsCopy.push(firstPointCopy);
    }

    return pointsCopy;
};

export const circleFromQueryParams = (latitude: number, longitude: number, radius: number): ICircle => {
    const guid = uuid();
    return {
        polygonType: PolygonType.Circle,
        id: guid,
        name: guid,
        isSelected: false,
        borderColor: "#FFFFFF",
        borderSize: 1,
        center: new google.maps.LatLng(latitude, longitude),
        radiusMeters: radius
    };
};

export const customPolygonFromPoints = (points: IPointBase[], isSgPolygon?: boolean): ICustomPolygon => {
    const finalPoints: google.maps.LatLng[] = points.map((point: IPointBase) => {
        return new google.maps.LatLng(point.y, point.x);
    });

    const guid = uuid();

    return {
        polygonType: PolygonType.Custom,
        id: guid,
        name: guid,
        isSelected: false,
        borderColor: "#FFFFFF",
        borderSize: 1,
        points: finalPoints,
        isSgPolygon,
    };
};

export const getCentroid = (polygon: IPolygon): google.maps.LatLng => {
    let points: ILatLon[] = [];
    switch (getPolygonType(polygon)) {
        case PolygonType.Circle: {
            const circle = polygon as ICircle;
            return circle.center;
        }
        case PolygonType.Rectangle: {
            const rectangle = polygon as IRectangle;
            points = rectangleToPoints(rectangle);
            break;
        }
        case PolygonType.Custom:{
            const custom = polygon as ICustomPolygon;
            points = customPolygonToPoints(custom);
            break;
        }
        default: {
            console.error("Invalid polygon type");
        }
    }

    return getCentroidFromPoints(points);
};

export const getCentroidFromPoints = (points: ILatLon[]): google.maps.LatLng => {

    const turfPoints: number[][] = points.map((point) => {
        return [point.longitude, point.latitude];
    });

    const polygon = turf.polygon([turfPoints]);
    const centroid = turf.centroid(polygon);
    const lat: number = centroid.geometry.coordinates[1];
    const lon: number = centroid.geometry.coordinates[0];

    return new google.maps.LatLng(lat, lon);
};

export const polygonSelfIntersects = (polygon: IPolygon): boolean => {

    const points: ILatLon[] = polygonToPoints(polygon);
    const turfPoints: number[][] = points.map((point) => {
        return [point.longitude, point.latitude];
    });
    const turfPolygon = turf.polygon([turfPoints]);
    const kinks = turf.kinks(turfPolygon);

    return kinks.features.length > 0;
};

/** NOTE: GB
 * Everything below this line is a port from "scout.logic.polygonlogic.js".
 *
 * I've left the functions mostly unchanged, minus a few tweaks to get things to compile here.
 * I'm not sure yet which functions will need to be exported, and which will not, so for
 * now I've left them all as non-exported. This will need some pretty heavy review
 */

const closeGeoJSONPolygon = (geoJSON) => {
    if (geoJSON.type === "Polygon") {
        closeSinglePolygon(geoJSON.coordinates);
    } else if (geoJSON.type === "MultiPolygon") {
        closeMultiPolygon(geoJSON.coordinates);
    }
};

const closeSinglePolygon = (coordinateWrapperArray) => {
    const coordinates = coordinateWrapperArray[0];
    const firstCoordinate = coordinates[0];
    const lastCoordinate = coordinates[coordinates.length - 1];

    if (!(firstCoordinate[0] === lastCoordinate[0] && firstCoordinate[1] === lastCoordinate[1])) {
        const firstCoordinateCopy = [...firstCoordinate];
        coordinates.push(firstCoordinateCopy);
    }
};

const closeMultiPolygon = (coordinates):void => {
    for (const polyCoordinates of coordinates) {
        closeSinglePolygon(polyCoordinates);
    }
};

const wktToFeatureBase = (wkt: string) => {
    const geo = parse(wkt);
    const feature = turf.feature(geo);

    return feature;
};

export const wktToFeature = memoize(wktToFeatureBase);

export const featureIntersectsUserPolygon = (feature: any, userWKT: string): boolean => {
    const userFeature = wktToFeature(userWKT);
    return turf.intersect(feature, userFeature) !== undefined;
};

export const polygonIntersectsPolygon = (polygon: IPolygon, wkt: string): boolean => {
    const points = polygonToPoints(polygon);
    const newPolygon = pointsToPolygon(points);
    const userPolygon = wktToFeature(wkt);

    const intersect = booleanIntersects(newPolygon, userPolygon);
    return intersect;
};

export const polygonCompletelyInsidePolygon = (polygon: IPolygon, wkt: string): boolean => {
    const points = polygonToPoints(polygon);
    const newPolygon = pointsToPolygon(points);
    const userPolygon = wktToFeature(wkt);

    const intersect = booleanWithin(newPolygon, userPolygon);
    return intersect;
};

export const pointIntersectsPolygon = (lat: number, lon: number, wkt: string): boolean => {
    if (!wkt || wkt.trim() === "") {
        return false;
    }

    const point = turf.point([lon, lat]);
    const userPolygon = wktToFeature(wkt);

    const intersect = booleanPointInPolygon(point, userPolygon);
    return intersect;
};